import { Link, useParams } from 'react-router'
import { motion } from 'motion/react'
import { ArrowLeft, Calendar, MapPin, Ticket, Users } from 'lucide-react'
import { useMatch, useSettings } from '@/lib/queries'
import { fileUrl } from '@/lib/pb'
import {
  cn,
  formatDate,
  formatTime,
  initials,
  plainText,
  readMatch,
  stageLabel,
  statusLabel,
} from '@/lib/utils'
import { Container, MeshTexture } from '@/components/site/primitives'
import { Button } from '@/components/ui/button'
import { ErrorState, LoadingBlock } from '@/components/ui/feedback'
import { usePageMeta } from '@/components/layout/site-layout'

export default function MatchPage() {
  const { id } = useParams<{ id: string }>()
  const { data: match, isLoading, isError } = useMatch(id)
  const { data: settings } = useSettings()

  const clubName = settings?.club_name ?? 'AFC Vikings Cairo'
  const title = match ? `${match.home_team} v ${match.away_team}` : undefined

  usePageMeta(title, match ? plainText(match.report, 200) || title : undefined)

  if (isLoading)
    return (
      <LoadingBlock
        className="min-h-[70vh] pt-[calc(var(--header-h)+4rem)]"
        label="Loading match"
      />
    )

  if (isError || !match) {
    return (
      <Container className="pt-[calc(var(--header-h)+5rem)] pb-24">
        <ErrorState
          title="Match not found"
          action={
            <Button asChild variant="outline">
              <Link to="/matches">All matches</Link>
            </Button>
          }
        />
      </Container>
    )
  }

  const view = readMatch(match, clubName)
  const competition = match.expand?.competition
  const gallery = Array.isArray(match.gallery) ? match.gallery : []

  const meta = [
    { Icon: Calendar, value: `${formatDate(match.kickoff)} · ${formatTime(match.kickoff)}` },
    match.venue ? { Icon: MapPin, value: match.venue } : null,
    match.attendance ? { Icon: Users, value: `${match.attendance} in attendance` } : null,
  ].filter(Boolean) as { Icon: typeof Calendar; value: string }[]

  return (
    <>
      <section className="border-hairline bg-ink relative overflow-hidden border-b pt-[calc(var(--header-h)+2rem)] pb-14 md:pt-[calc(var(--header-h)+3rem)] md:pb-20">
        <MeshTexture opacity={0.05} />

        <Container className="relative">
          <Button asChild variant="ghost" size="sm" className="mb-8 -ml-3">
            <Link to="/matches">
              <ArrowLeft className="size-4" aria-hidden />
              Matches
            </Link>
          </Button>

          <div className="flex flex-wrap items-center gap-3">
            {competition ? (
              <span className="condensed border-hairline text-bone-dim border px-2.5 py-1 text-[0.6875rem] font-semibold tracking-[0.16em]">
                {competition.name}
                {competition.season ? ` · ${competition.season}` : ''}
              </span>
            ) : null}
            {match.stage ? (
              <span className="condensed border-hairline text-bone-dim border px-2.5 py-1 text-[0.6875rem] font-semibold tracking-[0.16em]">
                {stageLabel(match.stage)}
                {match.group_name ? ` · ${match.group_name}` : ''}
              </span>
            ) : null}
            <span
              className={cn(
                'condensed px-2.5 py-1 text-[0.6875rem] font-bold tracking-[0.16em]',
                view.isLive ? 'bg-blood text-bone' : 'border-hairline text-bone-faint border'
              )}
            >
              {view.isLive && match.status === 'live' && match.live_minute
                ? `Live ${match.live_minute}′`
                : statusLabel(match.status)}
            </span>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 grid grid-cols-1 items-center gap-8 sm:grid-cols-[1fr_auto_1fr]"
          >
            <Side
              name={match.home_team}
              crest={fileUrl(match, match.home_crest, '200x200')}
              isClub={view.side === 'home'}
              align="right"
            />

            <div className="text-center">
              {view.hasScore ? (
                <div className="display text-bone text-[clamp(3.5rem,12vw,7rem)] leading-none whitespace-nowrap tabular-nums">
                  {match.home_score ?? 0}
                  <span className="text-blood mx-3">–</span>
                  {match.away_score ?? 0}
                </div>
              ) : (
                <div className="display text-bone text-[clamp(2.5rem,8vw,4.5rem)] leading-none">
                  {formatTime(match.kickoff)}
                </div>
              )}
              {view.isLive ? (
                <p className="condensed text-blood-bright mt-3 flex items-center justify-center gap-2 text-[0.75rem] font-bold tracking-[0.2em]">
                  <span className="animate-pulse-live bg-blood size-2 rounded-full" aria-hidden />
                  {match.status === 'halftime' ? 'Half-time' : 'In progress'}
                </p>
              ) : null}
            </div>

            <Side
              name={match.away_team}
              crest={fileUrl(match, match.away_crest, '200x200')}
              isClub={view.side === 'away'}
              align="left"
            />
          </motion.div>

          <div className="border-hairline text-bone-dim mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 border-t pt-8 text-sm">
            {meta.map(({ Icon, value }) => (
              <span key={value} className="flex items-center gap-2">
                <Icon className="text-bone-faint size-4" aria-hidden />
                {value}
              </span>
            ))}
          </div>

          {(match.ticket_url && view.isUpcoming) || match.highlights_url ? (
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              {match.ticket_url && view.isUpcoming ? (
                <Button asChild>
                  <a href={match.ticket_url} target="_blank" rel="noreferrer noopener">
                    <Ticket className="size-4" aria-hidden />
                    Tickets
                  </a>
                </Button>
              ) : null}
              {match.highlights_url ? (
                <Button asChild variant="outline">
                  <a href={match.highlights_url} target="_blank" rel="noreferrer noopener">
                    Watch highlights
                  </a>
                </Button>
              ) : null}
            </div>
          ) : null}
        </Container>
      </section>

      {match.report ? (
        <section className="border-hairline bg-ink border-b py-14 md:py-20">
          <Container size="narrow">
            <p className="eyebrow text-blood mb-6">Match report</p>
            <div
              className="prose-viking text-[1.0625rem]"
              dangerouslySetInnerHTML={{ __html: match.report }}
            />
          </Container>
        </section>
      ) : null}

      {gallery.length > 0 ? (
        <section className="bg-ink-sunken py-14 md:py-20">
          <Container>
            <p className="eyebrow text-blood mb-6">From the day</p>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-4">
              {gallery.map((filename) => (
                <a
                  key={filename}
                  href={fileUrl(match, filename)}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="group bg-ink-raised relative aspect-square overflow-hidden"
                >
                  <img
                    src={fileUrl(match, filename, '400x400')}
                    alt=""
                    loading="lazy"
                    className="size-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </a>
              ))}
            </div>
          </Container>
        </section>
      ) : null}
    </>
  )
}

function Side({
  name,
  crest,
  isClub,
  align,
}: {
  name: string
  crest?: string
  isClub: boolean
  align: 'left' | 'right'
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-4',
        align === 'right' ? 'sm:flex-row-reverse sm:text-right' : 'sm:text-left'
      )}
    >
      {crest ? (
        <img src={crest} alt="" className="size-14 shrink-0 object-contain md:size-20" />
      ) : (
        <span
          className="condensed border-hairline text-bone-faint flex size-14 shrink-0 items-center justify-center border text-base font-bold md:size-20 md:text-xl"
          aria-hidden
        >
          {initials(name)}
        </span>
      )}
      <h1
        className={cn(
          'condensed text-[clamp(1.25rem,3.2vw,2rem)] leading-tight font-bold tracking-[0.04em]',
          isClub ? 'text-bone' : 'text-bone-dim'
        )}
      >
        {name}
      </h1>
    </div>
  )
}
